import {useState } from 'react';
import {Outlet, NavLink, useNavigate, Link } from 'react-router-dom';
import {useAuth } from '../../hooks/useAuth';
import {useTheme } from '../../hooks/useTheme';
import { ArrowLeft, BarChart3, Database, FolderCog, KeyRound, LayoutDashboard, LayoutGrid, List, LogOut, MailCheck, Menu, Moon, ScrollText, Settings, Shield, Sliders, Sun, Trash2, UserCog, Users } from 'lucide-react';
import { getPermLevel } from '../../utils/rolesConfig';

const adminNav = [
  { to:'/admin',           icon:LayoutDashboard, label:'Dashboard',          end:true },
  { to:'/admin/users',     icon:Users,           label:'User Management' },
  { to:'/admin/roles',         icon:Shield,    label:'Roles & Permissions' },
  { to:'/admin/role-management', icon:UserCog,   label:'Role Management'     },
  { to:'/admin/page-access',     icon:KeyRound,  label:'Page Access'         },
  { to:'/admin/audit-logs',icon:ScrollText,      label:'Audit Logs' },
  { to:'/admin/settings',  icon:Settings,        label:'System Settings' },
  { to:'/admin/data',      icon:Database,        label:'Data Management' },
  { to:'/admin/recycle-bin', icon:Trash2,   label:'Recycle Bin' },
  { to:'/admin/email-log',  icon:MailCheck, label:'Email Log' },
  { to:'/admin/analytics',     icon:BarChart3,  label:'Analytics'      },
  { to:'/admin/project-types',  icon:FolderCog,  label:'Project Types'  },
  { to:'/admin/masters',        icon:List,       label:'Masters'        },
  { to:'/admin/metadata',       icon:Sliders, label:'Custom Fields'   },
  { to:'/admin/platforms',      icon:LayoutGrid, label:'Platforms'    },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  // Level, not literal: a new SystemAdmin-level role would otherwise be
  // silently demoted to the limited nav.
  const isSystemAdmin = getPermLevel(user?.role) === 'SystemAdmin';
  const filteredNav = adminNav.filter(item =>
    isSystemAdmin || ['/admin', '/admin/users', '/admin/masters', '/admin/roles', '/admin/analytics', '/admin/page-access', '/admin/role-management', '/admin/metadata', '/admin/platforms', '/admin/recycle-bin', '/admin/email-log'].includes(item.to)
  );
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const handleLogout = () => { logout(); navigate('/login'); };
  const initials = user?.name?.split(' ').map(n=>n[0]).join('').slice(0,2).toUpperCase()||'A';

  const Sidebar = () => (
    <div className="flex flex-col h-full">
      <div className="p-5 border-b border-white/10">
        <div className="flex items-center gap-3">
<svg width="36" height="36" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg" className="flex-shrink-0">
              <rect width="40" height="40" rx="9" fill="#1A2F5E"/>
              <line x1="9" y1="13" x2="27" y2="13" stroke="white" strokeWidth="3" strokeLinecap="round"/>
              <line x1="9" y1="20" x2="27" y2="20" stroke="white" strokeWidth="3" strokeLinecap="round"/>
              <line x1="9" y1="27" x2="20" y2="27" stroke="white" strokeWidth="3" strokeLinecap="round"/>
              <polyline points="21,24 25,28 33,18" stroke="#5CC3BC" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
            </svg>
          <div><p className="font-bold text-white text-sm leading-none">Admin Panel</p><p className="text-xs text-white/40 mt-0.5">Task Management System &middot; Q-Lana</p></div>
        </div>
      </div>
      <div className="px-3 pt-3">
        <Link to="/" onClick={() => setOpen(false)} className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs text-white/50 hover:text-white hover:bg-white/10 transition-colors">
          <ArrowLeft size={13} /> Back to Application
        </Link>
      </div>
      <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
        <p className="px-3 py-2 text-xs font-semibold text-white/30 uppercase tracking-widest">Modules</p>
        {filteredNav.map(({ to, icon:Icon, label, end }) => (
          <NavLink key={to} to={to} end={end} onClick={() => setOpen(false)}
            className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${isActive ? 'bg-[#2E539F] text-white' : 'text-white/60 hover:text-white hover:bg-white/10'}`}>
            <Icon size={16} className="flex-shrink-0" /><span>{label}</span>
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-white/10 space-y-1">
        <button onClick={toggleTheme} className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-xs text-white/50 hover:text-white hover:bg-white/10 transition-colors">
          {theme === 'dark' ? <Sun size={13} /> : <Moon size={13} />}
          {theme === 'dark' ? 'Switch to Light' : 'Switch to Dark'}
        </button>
        <div className="flex items-center gap-3 p-2 rounded-lg">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center font-bold text-white text-xs" style={{ backgroundColor: user?.avatar_color || '#ef4444' }}>{initials}</div>
          <div className="flex-1 min-w-0"><p className="text-sm font-medium text-white truncate">{user?.name}</p><p className="text-xs text-white/40">{user?.role}</p></div>
          <button onClick={handleLogout} className="p-1.5 rounded text-white/40 hover:text-red-400 transition-colors"><LogOut size={14} /></button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex h-screen overflow-hidden" style={{ background:'var(--bg)' }}>
      <aside className="hidden lg:flex w-64 flex-shrink-0 flex-col bg-gray-900"><Sidebar /></aside>
      {open && <div className="fixed inset-0 z-50 lg:hidden"><div className="absolute inset-0 bg-black/60" onClick={() => setOpen(false)} /><aside className="absolute left-0 top-0 h-full w-64 bg-gray-900"><Sidebar /></aside></div>}
      <main className="flex-1 flex flex-col overflow-hidden">
        <header className="lg:hidden flex items-center gap-3 p-4 border-b" style={{ background:'var(--surface)', borderColor:'var(--border)' }}>
          <button onClick={() => setOpen(true)} className="p-2 rounded-lg hover:bg-[var(--surface-alt)]"><Menu size={20} style={{ color:'var(--text)' }} /></button>
          <div className="flex items-center gap-2"><svg width="24" height="24" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect width="40" height="40" rx="9" fill="#1A2F5E"/>
              <line x1="9" y1="13" x2="27" y2="13" stroke="white" strokeWidth="3" strokeLinecap="round"/>
              <line x1="9" y1="20" x2="27" y2="20" stroke="white" strokeWidth="3" strokeLinecap="round"/>
              <line x1="9" y1="27" x2="20" y2="27" stroke="white" strokeWidth="3" strokeLinecap="round"/>
              <polyline points="21,24 25,28 33,18" stroke="#5CC3BC" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
            </svg><span className="font-bold text-sm" style={{ color:'var(--text)' }}>Admin Panel</span></div>
        </header>
        <div className="flex-1 overflow-y-auto"><div className="p-6 max-w-screen-2xl mx-auto animate-fade-in"><Outlet /></div></div>
      </main>
    </div>
  );
}
